import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Catches render errors in children and shows a fallback (or error message)
 * instead of a white screen. Use around routes or heavy components to see what broke.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
    if (typeof console !== 'undefined' && console.error) {
      console.error('[ErrorBoundary]', error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      const msg = this.state.error.message || String(this.state.error);
      const stack = this.state.errorInfo?.componentStack;
      return (
        <div
          style={{
            padding: 24,
            maxWidth: 600,
            margin: '24px auto',
            background: '#1a1a1a',
            color: '#e5e5e5',
            fontFamily: 'system-ui, sans-serif',
            borderRadius: 8,
            border: '1px solid #ef4444',
          }}
        >
          <h2 style={{ color: '#f87171', marginTop: 0 }}>Something went wrong</h2>
          <p style={{ wordBreak: 'break-word' }}>{msg}</p>
          {stack && (
            <pre
              style={{
                fontSize: 11,
                overflow: 'auto',
                background: '#0f0f0f',
                padding: 12,
                borderRadius: 4,
                marginTop: 12,
              }}
            >
              {stack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
